// app/lib/analytics.ts
//
// PostHog wrapper. Single chokepoint for product analytics, feature flags,
// session replay, and error tracking. All public functions silently no-op
// when not initialized — so dev-without-key, tests, and CI all work without
// any PostHog account.
//
// Init runs once from root.tsx Layout effect (Phase 4c Task 4). The
// module-level `initialized` guard prevents HMR re-init.

import posthog from "posthog-js";
import { useEffect, useState } from "react";

import type {
  AuthSignedInProps,
  AuthSignInFailedProps,
  AuthSignedOutProps,
  ChatRegeneratedProps,
  ChatSentProps,
  ContentAddedProps,
  ContentDeletedProps,
  CopyFormatProps,
  DrawerOpenedProps,
  ErrorContext,
  FeedbackSubmittedProps,
  KnowledgeViewedProps,
  PaletteOpenedProps,
  SearchSubmittedProps,
  ThemeChangedProps,
} from "./analytics.types";

let initialized = false;

/** Bootstrap PostHog. Called once from root.tsx Layout. Subsequent calls
 *  are ignored (HMR safety). No-op if VITE_POSTHOG_KEY is missing. */
export function initAnalytics(): void {
  if (initialized) return;
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) return; // dev or test without key — silent no-op forever
  const host = import.meta.env.VITE_POSTHOG_HOST ?? "https://us.i.posthog.com";
  const release = import.meta.env.VITE_RELEASE_VERSION ?? "dev";

  posthog.init(key, {
    api_host: host,
    autocapture: {
      dom_event_allowlist: ["click", "submit"],
      element_allowlist: ["a", "button", "form"],
    },
    capture_pageview: true,
    capture_pageleave: true,
    capture_exceptions: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: ".ph-mask, [data-ph-no-capture]",
      maskInputOptions: { password: true, email: true, text: true },
      blockSelector: ".ph-block",
      // Network payload capture is OFF by default in posthog-js. Declared
      // explicitly here so future maintainers don't silently enable it. Bot
      // SSE responses contain warehouse text — recording network would
      // defeat the masking config above.
      network_payload_capture: { recordBody: false, recordHeaders: false },
      recordCrossOriginIframes: false,
    } as Record<string, unknown>,
    disable_session_recording: import.meta.env.DEV,
    loaded: (ph) => {
      ph.register({ $release: release });
    },
    before_send: (event) => event, // pass-through; scrubbing in mask config
  });

  initialized = true;
}

/** Forward-compat for an auth proxy (GCP IAP etc.). Until that lands, this
 *  function is unused — anonymous cookie identification is the default. */
export function identifyUser(distinctId: string, traits?: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.identify(distinctId, traits);
}

/** Imperative event capture — used by the named track* functions below. */
function capture(event: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.capture(event, properties);
}

// --- Named event functions (10 total — see spec §5) ---

export function trackChatSent(props: ChatSentProps): void {
  capture("chat_sent", { ...props });
}
export function trackChatRegenerated(props: ChatRegeneratedProps): void {
  capture("chat_regenerated", { ...props });
}
export function trackCopyFormatUsed(props: CopyFormatProps): void {
  capture("copy_format_used", { ...props });
}
export function trackFeedbackSubmitted(props: FeedbackSubmittedProps): void {
  capture("feedback_submitted", { ...props });
}
export function trackKnowledgeViewed(props: KnowledgeViewedProps): void {
  capture("knowledge_viewed", { ...props });
}
export function trackSearchSubmitted(props: SearchSubmittedProps): void {
  capture("search_submitted", { ...props });
}
export function trackContentAdded(props: ContentAddedProps): void {
  capture("content_added", { ...props });
}
export function trackContentDeleted(props: ContentDeletedProps): void {
  capture("content_deleted", { ...props });
}
export function trackThemeChanged(props: ThemeChangedProps): void {
  capture("theme_changed", { ...props });
}
export function trackDrawerOpened(props: DrawerOpenedProps): void {
  capture("drawer_opened", { ...props });
}
export function trackPaletteOpened(props: PaletteOpenedProps): void {
  capture("palette_opened", { ...props });
}

// --- Auth lifecycle (Phase 5) ---

export function trackAuthSignedIn(props: AuthSignedInProps): void {
  capture("auth_signed_in", { ...props });
}
export function trackAuthSignInFailed(props: AuthSignInFailedProps): void {
  capture("auth_sign_in_failed", { ...props });
}
export function trackAuthSignedOut(props: AuthSignedOutProps): void {
  capture("auth_signed_out", { ...props });
}

/** Reset PostHog identity. Call from logout so subsequent anonymous events
 *  aren't attributed to the previous user. */
export function clearUser(): void {
  if (!initialized) return;
  posthog.reset();
}

// --- Errors ---

/** Capture an exception with optional structured context. Idempotent —
 *  PostHog dedupes identical stacks within a session. */
export function captureException(error: unknown, context?: ErrorContext): void {
  if (!initialized) return;
  const err = error instanceof Error ? error : new Error(String(error));
  posthog.captureException(err, context);
}

// --- Feature flags ---

/** React hook for boolean PostHog feature flags. Returns `undefined` until
 *  PostHog has fetched flag definitions (~100-300ms after init). Consumers
 *  should treat undefined as the safe default. */
export function useFeatureFlag(key: string): boolean | undefined {
  const [value, setValue] = useState<boolean | undefined>(() => {
    if (!initialized) return undefined;
    const v = posthog.isFeatureEnabled(key);
    return typeof v === "boolean" ? v : undefined;
  });

  useEffect(() => {
    if (!initialized) return;
    // Subscribe to flag changes (PostHog fires this when remote flags load
    // or when an experiment overrides the value).
    const unsub = posthog.onFeatureFlags(() => {
      const v = posthog.isFeatureEnabled(key);
      setValue(typeof v === "boolean" ? v : undefined);
    });
    return () => {
      unsub?.();
    };
  }, [key]);

  return value;
}

// --- Session correlation ---

/** Return the active PostHog session ID, or null when PostHog isn't
 *  initialized. Used by the chat-send path to forward `$session_id` as a
 *  header so the RAG service's $ai_generation / $ai_embedding events can
 *  be linked to the user's session replay in PostHog. */
export function getSessionId(): string | null {
  if (!initialized) return null;
  try {
    return posthog.get_session_id() || null;
  } catch {
    return null;
  }
}

// --- Test helper ---

/** Reset module state. Vitest only — gated on test env. */
export function __resetForTest(): void {
  if (process.env.NODE_ENV !== "test") {
    return;
  }
  initialized = false;
}
